import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonMessageWidget } from '@eforall/common';
import { DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { AccApplicationDetails } from '@interfaces';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { environment } from '../../../environments';
import { AppService } from '../../../services';
import { AccApplicationDialogDataService } from './acc-application-dialog-data.service';
import { ApplicationDialogAlignedAcceleratorsPart } from './aligned-accelerators/aligned-accelerators.part';
import { ApplicationDialogAlignedCommitmentsPart } from './aligned-commitments/aligned-commitments.part';
import { AccApplicationContactDialogPart } from './contact/acc-application-contact-dialog.part';
import { AccApplicationDialogInterviewPart } from './interview/interview.part';
import { AccApplicationManagementDialogPart } from './management/management-dialog.part';
import { OverviewContentDialogPart } from './overview-content/overview-content-dialog.part';
import { AccApplicationDialogSchedulePart } from './schedule/schedule.part';
import { StatusLogDialogPart } from './status-log/status-log-dialog.part';

@Component({
	selector: 'acc-application-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		TabStripModule,
		StatusLogDialogPart,
		AccApplicationContactDialogPart,
		OverviewContentDialogPart,
		AccApplicationDialogSchedulePart,
		ApplicationDialogAlignedCommitmentsPart,
		ApplicationDialogAlignedAcceleratorsPart,
		CommonMessageWidget,
		AccApplicationDialogInterviewPart,
		AccApplicationManagementDialogPart
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './acc-application.dialog.html'
})

export class AccApplicationDialog implements OnInit {

	public dialogRef = inject(MatDialogRef<AccApplicationDialog>);
	public dialogContext: DialogContext<{ accApplicationId: number }> = inject(MAT_DIALOG_DATA);

	public dialogBodyHeight = computed(() => this.dialogContext.dimensions().bodyHeight);

	private accApplService = inject(AccApplicationDialogDataService);
	private app = inject(AppService);

	public isNotProdEnvironment = environment.name !== 'PROD';

	public readonly canManageApplication = computed(() => {
		const permissions = this.app.data().permissions;
		return permissions.isSoftwareDev || permissions.canManageAdmissionsStaff;
	});

	public readonly headerName = computed(() => {
		const details = this.accApplService.dialogData()?.details;
		if (!details) return 'Application';
		return `Application: ${details.ventureName}`
	})

	async ngOnInit() {
		const data = this.dialogContext.data;
		if (data) {
			await this.accApplService.loadData(data.accApplicationId);
		}
	}

	public actions = computed<DialogAction[]>(() => {
		return [{
			id: 'close',
			cssClass: signal(undefined),
			enabled: signal(true),
			visible: signal(true),
			text: signal('Close'),
			linkType: 'no-action',
			willCloseDialog: true,
		}]
	});

	public accApplicationDetails = computed<AccApplicationDetails | undefined>(() => {
		return this.accApplService.dialogData()?.details;
	});
}